import React, { useEffect, useState } from 'react';

import CatalogPicker from './CatalogPicker';
import ProvenanceChip, { EmptyProvenanceChip } from './ProvenanceChip';
import {
  BagSlot,
  BagSlotUpdate,
  CatalogSelection,
  CatalogTable,
  DistanceConfidence,
} from '../../types/bag.types';

export interface SlotRowProps {
  slot: BagSlot;
  onChange: (patch: BagSlotUpdate) => void;
  onConfirm: () => void;
  disabled?: boolean;
}

/**
 * Fixed, and shown on every rung (MY_BAG_PLAN.md §2.2). Players quote their
 * best-ever unevenly, and gapping is arithmetic on differences, so uneven
 * optimism manufactures holes that aren't there.
 */
const CARRY_HELP = 'your stock, solid-strike carry — not your best one.';

const CATALOG_TABLE: Partial<Record<BagSlot['club_category'], CatalogTable>> = {
  iron: 'irons',
  wedge: 'wedges',
};

const numberFieldClass =
  'min-h-[44px] w-24 rounded-brand border border-line-2 bg-paper px-2 font-mono tabular-nums text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none disabled:opacity-50';

const textFieldClass =
  'min-h-[44px] flex-1 rounded-brand border border-line-2 bg-paper px-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none disabled:opacity-50';

function toText(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

export default function SlotRow({
  slot,
  onChange,
  onConfirm,
  disabled = false,
}: SlotRowProps): JSX.Element {
  const [carryText, setCarryText] = useState(toText(slot.carry_yards));
  const [totalText, setTotalText] = useState(toText(slot.total_yards));

  useEffect(() => {
    setCarryText(toText(slot.carry_yards));
  }, [slot.carry_yards]);

  useEffect(() => {
    setTotalText(toText(slot.total_yards));
  }, [slot.total_yards]);

  const isPutter = slot.club_category === 'putter';
  const catalogTable = CATALOG_TABLE[slot.club_category];
  const benched = !slot.in_bag;

  /* Typing a carry is the golfer telling us the number, so it promotes the rung
     to `entered` — the same promotion tap-confirm performs. An `estimated` rung
     can never drive a bend recommendation (§2.4), so this is the whole point of
     the flow, not a convenience. */
  const handleCarry = (text: string) => {
    setCarryText(text);
    if (text.trim() === '') {
      onChange({ carry_yards: null, distance_provenance: 'entered' });
      return;
    }
    const parsed = parseFloat(text);
    if (isNaN(parsed)) return;
    onChange({ carry_yards: parsed, distance_provenance: 'entered' });
  };

  const handleTotal = (text: string) => {
    setTotalText(text);
    if (text.trim() === '') {
      onChange({ total_yards: null });
      return;
    }
    const parsed = parseFloat(text);
    if (isNaN(parsed)) return;
    onChange({ total_yards: parsed });
  };

  /* `min`/`max` on a number input constrain the spinner, not what can be typed,
     and the API validates carry at 10-400 / total at 10-450 across the *whole*
     ladder in one transaction — so one out-of-range rung 422s all fourteen.
     Saying so here, at the rung, is the only place the golfer can act on it. */
  const carryRangeError =
    slot.carry_yards != null && (slot.carry_yards < 10 || slot.carry_yards > 400)
      ? 'Carry must be between 10 and 400 yards — the bag will not save until this is fixed.'
      : null;
  const totalRangeError =
    slot.total_yards != null && (slot.total_yards < 10 || slot.total_yards > 450)
      ? 'Total must be between 10 and 450 yards — the bag will not save until this is fixed.'
      : null;
  const totalOrderError =
    slot.total_yards != null && slot.carry_yards != null && slot.total_yards < slot.carry_yards
      ? 'Total cannot be shorter than carry — total is carry plus roll.'
      : null;
  const rangeError = carryRangeError || totalRangeError || totalOrderError;

  const handleCatalog = (selection: CatalogSelection | null) => {
    if (!selection) {
      onChange({ catalog_table: null, catalog_id: null });
      return;
    }
    const patch: BagSlotUpdate = {
      catalog_table: selection.catalog_table,
      catalog_id: selection.catalog_id,
      free_text_brand: selection.free_text_brand,
      free_text_model: selection.free_text_model,
    };
    if (selection.loft != null) {
      patch.loft = selection.loft;
      patch.loft_source = 'catalog';
    }
    onChange(patch);
  };

  const setConfidence = (next: DistanceConfidence) => {
    onChange({ distance_confidence: slot.distance_confidence === next ? null : next });
  };

  const confidenceButton = (next: DistanceConfidence, label: string) => {
    const active = slot.distance_confidence === next;
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setConfidence(next)}
        aria-pressed={active}
        className={`min-h-[44px] px-3 text-sm ${
          active ? 'bg-slate text-white' : 'bg-paper text-ink-2 hover:bg-ground'
        } disabled:opacity-50`}
      >
        {label}
      </button>
    );
  };

  const showConfirm = !isPutter && slot.distance_provenance === 'estimated';

  return (
    <div
      className={`rounded-brand border bg-paper p-3 ${
        benched ? 'border-line opacity-60' : 'border-line-2'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-md font-semibold tabular-nums text-ink">
            {slot.club_label}
          </span>
          <span className="text-xs uppercase tracking-wide text-ink-3">
            {slot.club_category}
          </span>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ in_bag: !slot.in_bag })}
          aria-pressed={slot.in_bag}
          className={`min-h-[44px] rounded-brand border px-3 text-sm ${
            slot.in_bag
              ? 'border-line-strong bg-ground text-ink'
              : 'border-dashed border-line-2 text-ink-3'
          } disabled:opacity-50`}
        >
          {slot.in_bag ? 'In bag' : 'Benched'}
        </button>
      </div>

      <div className="mt-2 flex flex-col gap-2">
        {catalogTable ? (
          <CatalogPicker
            table={catalogTable}
            value={{
              catalog_id: slot.catalog_id,
              free_text_brand: slot.free_text_brand,
              free_text_model: slot.free_text_model,
            }}
            onSelect={handleCatalog}
            disabled={disabled}
          />
        ) : null}

        {slot.catalog_id == null && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              disabled={disabled}
              value={slot.free_text_brand || ''}
              onChange={(e) => onChange({ free_text_brand: e.target.value || null })}
              placeholder="Brand"
              aria-label={`${slot.club_label} brand`}
              className={textFieldClass}
            />
            <input
              type="text"
              disabled={disabled}
              value={slot.free_text_model || ''}
              onChange={(e) => onChange({ free_text_model: e.target.value || null })}
              placeholder="Model"
              aria-label={`${slot.club_label} model`}
              className={textFieldClass}
            />
          </div>
        )}
      </div>

      {isPutter ? (
        <p className="mt-2 text-xs text-ink-3">
          The putter is one of the fourteen, but it holds no distance and never gaps.
        </p>
      ) : (
        <div className="mt-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-ink-3">Carry</span>
              <span className="flex items-center gap-1">
                <input
                  type="number"
                  inputMode="decimal"
                  min={10}
                  max={400}
                  disabled={disabled}
                  value={carryText}
                  onChange={(e) => handleCarry(e.target.value)}
                  placeholder="—"
                  aria-label={`${slot.club_label} carry, yards`}
                  className={numberFieldClass}
                />
                <span className="text-xs text-ink-3">yd</span>
              </span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs text-ink-3">Total (optional)</span>
              <span className="flex items-center gap-1">
                <input
                  type="number"
                  inputMode="decimal"
                  min={10}
                  max={450}
                  disabled={disabled}
                  value={totalText}
                  onChange={(e) => handleTotal(e.target.value)}
                  placeholder="—"
                  aria-label={`${slot.club_label} total, yards`}
                  className={numberFieldClass}
                />
                <span className="text-xs text-ink-3">yd</span>
              </span>
            </label>

            <div className="flex items-center gap-2 pb-1">
              {slot.carry_yards == null ? (
                <EmptyProvenanceChip />
              ) : (
                <ProvenanceChip
                  provenance={slot.distance_provenance}
                  confidence={slot.distance_confidence}
                />
              )}
              {showConfirm && (
                <button
                  type="button"
                  disabled={disabled || slot.carry_yards == null}
                  onClick={onConfirm}
                  className="min-h-[44px] rounded-brand bg-slate px-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  Confirm
                </button>
              )}
            </div>

            <div className="inline-flex overflow-hidden rounded-brand border border-line-2 pb-0">
              {confidenceButton('sure', 'Sure')}
              <span className="w-px bg-line-2" />
              {confidenceButton('roughly', 'Roughly')}
            </div>
          </div>

          {rangeError && (
            <p role="alert" className="mt-1 text-xs font-medium text-score-0">
              {rangeError}
            </p>
          )}
          <p className="mt-1 text-xs text-ink-3">{CARRY_HELP}</p>
        </div>
      )}
    </div>
  );
}
